import type { ScanDto } from '@compflash/contracts';
import { formatMoney } from '../lib/format';

type Valuation = NonNullable<ScanDto['valuation']>;

/**
 * Sold-price scatter with the fair-value line.
 *
 * Drawn as inline SVG rather than pulling in a charting library: the chart has
 * one job, and a dependency-free implementation keeps the live workspace bundle
 * small enough to load between auction lots.
 */
export function PriceHistoryChart({ valuation }: { valuation: Valuation }): JSX.Element | null {
  const points = valuation.comps
    .filter((comp) => comp.included && comp.adjustedTotal)
    .map((comp) => ({
      ageDays: Math.max(0, comp.ageDays),
      amount: comp.adjustedTotal!.amountMinor,
      adjusted: comp.gradeAdjustmentFactor !== null,
      title: comp.sale.title,
      soldAt: comp.sale.soldAt,
    }));

  const excluded = valuation.comps.filter((comp) => !comp.included).length;
  if (points.length === 0) return <p className="muted">No usable comparable sales to plot.</p>;

  const width = 640;
  const height = 200;
  const padding = { top: 12, right: 12, bottom: 26, left: 56 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const maxAge = Math.max(1, ...points.map((point) => point.ageDays));
  const amounts = points.map((point) => point.amount);
  const rawMin = Math.min(...amounts);
  const rawMax = Math.max(...amounts);
  const span = Math.max(1, rawMax - rawMin);
  const minValue = Math.max(0, rawMin - span * 0.12);
  const maxValue = rawMax + span * 0.12;

  // Oldest on the left, newest on the right: age decreases left to right.
  const x = (ageDays: number): number => padding.left + plotWidth * (1 - ageDays / maxAge);
  const y = (amount: number): number => padding.top + plotHeight * (1 - (amount - minValue) / (maxValue - minValue));

  const fairValue = valuation.fairValue?.amountMinor ?? null;
  const currency = valuation.currency;

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Comparable sale prices over time">
        <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} className="chart-plot" />

        {[0, 0.5, 1].map((fraction) => {
          const value = minValue + (maxValue - minValue) * (1 - fraction);
          const yy = padding.top + plotHeight * fraction;
          return (
            <g key={fraction}>
              <line x1={padding.left} x2={width - padding.right} y1={yy} y2={yy} className="chart-grid" />
              <text x={padding.left - 8} y={yy + 4} textAnchor="end" className="chart-axis">
                {formatMoney({ amountMinor: value, currency })}
              </text>
            </g>
          );
        })}

        {fairValue !== null && fairValue >= minValue && fairValue <= maxValue && (
          <>
            <line x1={padding.left} x2={width - padding.right} y1={y(fairValue)} y2={y(fairValue)} className="chart-fair" />
            <text x={width - padding.right} y={y(fairValue) - 5} textAnchor="end" className="chart-fair-label">
              fair value {formatMoney(valuation.fairValue)}
            </text>
          </>
        )}

        {points.map((point, index) => (
          <circle
            key={`${point.soldAt}-${index}`}
            cx={x(point.ageDays)}
            cy={y(point.amount)}
            r={4}
            className={point.adjusted ? 'chart-point adjusted' : 'chart-point'}
          >
            <title>{`${formatMoney({ amountMinor: point.amount, currency })} · ${new Date(point.soldAt).toLocaleDateString()}${point.adjusted ? ' · grade adjusted' : ''}\n${point.title}`}</title>
          </circle>
        ))}

        <text x={padding.left} y={height - 6} className="chart-axis">
          {Math.round(maxAge)} days ago
        </text>
        <text x={width - padding.right} y={height - 6} textAnchor="end" className="chart-axis">
          today
        </text>
      </svg>
      <figcaption className="muted small">
        {points.length} sale{points.length === 1 ? '' : 's'} plotted
        {excluded > 0 ? `, ${excluded} excluded by matching, window or outlier filters` : ''}. Hollow points were converted
        from a different grade.
      </figcaption>
    </figure>
  );
}
